/**
 * 🕹️ ARCADE EXTRAS — Stage Breaker · Tourbus Runner · Kaoss Synth X/Y.
 *
 * Pourquoi ce fichier : `/games` n'est plus seulement trois jeux classiques.
 * La décision user du 2026-11-04 (« un truc fun pour tuer le temps dans les
 * files d'attente ») a donné la page ; ce lot y ajoute la partie ARCADE
 * « physique » (casse-briques, runner) et le premier instrument jouable au
 * doigt (pad X/Y façon Kaoss Pad).
 *
 * RÈGLES (identiques à `gameKit`, elles tiennent le lot) :
 *  - ZÉRO dépendance externe, ZÉRO requête réseau : Canvas 2D + DOM +
 *    Web Audio. Un aéroport sans réseau n'a pas de CDN.
 *  - Chaque jeu est AUTO-CONTENU et monté UNIQUEMENT quand son onglet est
 *    actif : la boucle et les écouteurs clavier s'arrêtent avec lui.
 *  - Tactile D'ABORD : tout ce qui se tape fait ≥ 48 px (`game-ctl`), et le
 *    geste sur le canvas pilote directement le jeu.
 *  - Aucun `AudioContext` au montage (iOS/Chrome l'exigent ; jsdom n'en a
 *    pas) : le synthé naît au PREMIER contact avec le pad.
 *  - i18n 100 % anglais (`games.*`) : aucun libellé en dur.
 *
 * RÉFÉRENCE DU LOT : `bandz-frontend/references/ui-roadcase-redesign-2026.md`
 * (section LOT STAGE-TOOLBOX-AND-ARCADE-PLUS).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ScoreBar, StageOverlay, clamp, useGameStatus, useHighScore } from './gameKit';

/* ====================================================================== */
/*  🧱 Stage Breaker — casse-briques « mur d'amplis »                      */
/* ====================================================================== */

const BR_W = 320;
const BR_H = 320;
const BR_PADDLE_W = 68;
const BR_PADDLE_H = 10;
const BR_PADDLE_Y = BR_H - 26;
const BR_BALL_R = 5;
const BR_COLS = 8;
const BR_ROWS = 5;
const BR_PAD_X = 8;
const BR_TOP = 36;
const BR_BRICK_H = 16;
const BR_BRICK_GAP = 4;
const BR_SPEED = 3.1;
const BR_POINTS = 10;
const BR_LIVES = 3;

/** Couleurs par rangée : amplis ambre, spots cyan, subs violets… */
const BR_ROW_COLORS = ['#e0a44c', '#f5c469', '#5ad1e6', '#c084fc', '#00d47e'];

interface Brick {
  x: number;
  y: number;
  w: number;
  h: number;
  color: string;
  alive: boolean;
}

function buildBricks(): Brick[] {
  const usable = BR_W - BR_PAD_X * 2;
  const step = usable / BR_COLS;
  const out: Brick[] = [];
  for (let row = 0; row < BR_ROWS; row += 1) {
    for (let col = 0; col < BR_COLS; col += 1) {
      out.push({
        x: BR_PAD_X + col * step + BR_BRICK_GAP / 2,
        y: BR_TOP + row * (BR_BRICK_H + BR_BRICK_GAP),
        w: step - BR_BRICK_GAP,
        h: BR_BRICK_H,
        color: BR_ROW_COLORS[row % BR_ROW_COLORS.length],
        alive: true,
      });
    }
  }
  return out;
}

export function StageBreakerGame() {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const [score, setScore] = useState(0);
  const [best, bumpBest] = useHighScore('bandz.games.high.breaker');
  const [lives, setLives] = useState(BR_LIVES);
  const { status, statusRef, setStatus } = useGameStatus();

  const padXRef = useRef(BR_W / 2 - BR_PADDLE_W / 2);
  const ballRef = useRef({ x: BR_W / 2, y: BR_PADDLE_Y - 40, vx: BR_SPEED * 0.55, vy: -BR_SPEED });
  const bricksRef = useRef<Brick[]>(buildBricks());
  const scoreRef = useRef(0);
  const livesRef = useRef(BR_LIVES);
  const speedRef = useRef(1);
  const touchRef = useRef<number | null>(null);

  const draw = useCallback(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    if (!ctx) return;

    ctx.fillStyle = '#1b1915';
    ctx.fillRect(0, 0, BR_W, BR_H);

    // Sol de scène discret : repère visuel du raz-de-marée.
    ctx.strokeStyle = 'rgba(234, 231, 221, 0.07)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, BR_PADDLE_Y + BR_PADDLE_H + 8);
    ctx.lineTo(BR_W, BR_PADDLE_Y + BR_PADDLE_H + 8);
    ctx.stroke();

    bricksRef.current.forEach((brick) => {
      if (!brick.alive) return;
      ctx.fillStyle = brick.color;
      ctx.fillRect(brick.x, brick.y, brick.w, brick.h);
      // Trait supérieur plus clair : donne du relief à la brique.
      ctx.fillStyle = 'rgba(255, 255, 255, 0.18)';
      ctx.fillRect(brick.x, brick.y, brick.w, 2);
    });

    // La raquette : un flightcase ambre avec ses deux coins métal.
    ctx.fillStyle = '#e0a44c';
    ctx.fillRect(padXRef.current, BR_PADDLE_Y, BR_PADDLE_W, BR_PADDLE_H);
    ctx.fillStyle = '#f5c469';
    ctx.fillRect(padXRef.current, BR_PADDLE_Y, 8, BR_PADDLE_H);
    ctx.fillRect(padXRef.current + BR_PADDLE_W - 8, BR_PADDLE_Y, 8, BR_PADDLE_H);

    // La balle : une LED de signal.
    const ball = ballRef.current;
    ctx.fillStyle = '#00d47e';
    ctx.beginPath();
    ctx.arc(ball.x, ball.y, BR_BALL_R, 0, Math.PI * 2);
    ctx.fill();
  }, []);

  const resetBall = useCallback(() => {
    ballRef.current = {
      x: padXRef.current + BR_PADDLE_W / 2,
      y: BR_PADDLE_Y - 40,
      vx: BR_SPEED * 0.55 * (Math.random() > 0.5 ? 1 : -1),
      vy: -BR_SPEED * speedRef.current,
    };
  }, []);

  const resetGame = useCallback(() => {
    padXRef.current = BR_W / 2 - BR_PADDLE_W / 2;
    bricksRef.current = buildBricks();
    scoreRef.current = 0;
    livesRef.current = BR_LIVES;
    speedRef.current = 1;
    setScore(0);
    setLives(BR_LIVES);
    resetBall();
    draw();
  }, [draw, resetBall]);

  const endGame = useCallback(() => {
    setStatus('over');
    bumpBest(scoreRef.current);
    draw();
  }, [bumpBest, draw, setStatus]);

  const loseLife = useCallback(() => {
    livesRef.current -= 1;
    setLives(livesRef.current);
    if (livesRef.current <= 0) {
      endGame();
      return;
    }
    resetBall();
    draw();
  }, [draw, endGame, resetBall]);

  const step = useCallback(() => {
    if (statusRef.current !== 'running') return;
    const ball = ballRef.current;

    ball.x += ball.vx;
    ball.y += ball.vy;

    // Murs latéraux et plafond.
    if (ball.x < BR_BALL_R) {
      ball.x = BR_BALL_R;
      ball.vx = Math.abs(ball.vx);
    } else if (ball.x > BR_W - BR_BALL_R) {
      ball.x = BR_W - BR_BALL_R;
      ball.vx = -Math.abs(ball.vx);
    }
    if (ball.y < BR_BALL_R) {
      ball.y = BR_BALL_R;
      ball.vy = Math.abs(ball.vy);
    }

    // Raquette : rebond dont l'angle dépend du point d'impact.
    if (
      ball.vy > 0 &&
      ball.y + BR_BALL_R >= BR_PADDLE_Y &&
      ball.y - BR_BALL_R <= BR_PADDLE_Y + BR_PADDLE_H &&
      ball.x >= padXRef.current - BR_BALL_R &&
      ball.x <= padXRef.current + BR_PADDLE_W + BR_BALL_R
    ) {
      const rel = clamp((ball.x - (padXRef.current + BR_PADDLE_W / 2)) / (BR_PADDLE_W / 2), -1, 1);
      const speed = Math.min(7.6, Math.hypot(ball.vx, ball.vy) * 1.015);
      const angle = rel * 1.0;
      ball.vx = Math.sin(angle) * speed;
      ball.vy = -Math.abs(Math.cos(angle) * speed);
      ball.y = BR_PADDLE_Y - BR_BALL_R;
    }

    // Briques : première brique touchée = cassée.
    let hit = false;
    for (const brick of bricksRef.current) {
      if (!brick.alive) continue;
      if (
        ball.x + BR_BALL_R >= brick.x &&
        ball.x - BR_BALL_R <= brick.x + brick.w &&
        ball.y + BR_BALL_R >= brick.y &&
        ball.y - BR_BALL_R <= brick.y + brick.h
      ) {
        brick.alive = false;
        hit = true;
        scoreRef.current += BR_POINTS;
        setScore(scoreRef.current);
        ball.vy = -ball.vy;
        break;
      }
    }
    if (hit && bricksRef.current.every((b) => !b.alive)) {
      // Mur nettoyé : nouvelle vague, balle plus rapide — on ne s'arrête pas.
      speedRef.current += 0.12;
      bricksRef.current = buildBricks();
      ball.vx = clamp(ball.vx * 1.08, -7.6, 7.6);
      ball.vy = -clamp(Math.abs(ball.vy) * 1.08, BR_SPEED, 7.6);
    }

    if (ball.y > BR_H + BR_BALL_R) loseLife();

    draw();
  }, [draw, loseLife, statusRef]);

  const start = useCallback(() => {
    if (statusRef.current === 'paused') {
      setStatus('running');
      return;
    }
    resetGame();
    setStatus('running');
  }, [resetGame, setStatus, statusRef]);

  const toggle = useCallback(() => {
    if (statusRef.current === 'running') setStatus('paused');
    else start();
  }, [setStatus, start, statusRef]);

  const nudge = useCallback(
    (dir: number) => {
      padXRef.current = clamp(padXRef.current + dir * 26, 0, BR_W - BR_PADDLE_W);
      if (statusRef.current !== 'running') start();
      else draw();
    },
    [draw, start, statusRef],
  );

  const movePaddleTo = useCallback(
    (clientX: number) => {
      const cv = canvasRef.current;
      if (!cv) return;
      const rect = cv.getBoundingClientRect();
      if (rect.width === 0) return;
      const px = ((clientX - rect.left) / rect.width) * BR_W;
      padXRef.current = clamp(px - BR_PADDLE_W / 2, 0, BR_W - BR_PADDLE_W);
      if (statusRef.current === 'running') draw();
    },
    [draw, statusRef],
  );

  useEffect(() => {
    resetGame();
    const id = window.setInterval(step, 16);
    return () => window.clearInterval(id);
  }, [resetGame, step]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      if (key === 'arrowleft' || key === 'a') {
        event.preventDefault();
        nudge(-1);
      } else if (key === 'arrowright' || key === 'd') {
        event.preventDefault();
        nudge(1);
      } else if (key === ' ') {
        event.preventDefault();
        toggle();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [nudge, toggle]);

  const onPointerDown = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    touchRef.current = event.pointerId;
    movePaddleTo(event.clientX);
  };

  return (
    <section className="game-panel" data-game="breaker" data-game-panel="breaker">
      <ScoreBar
        score={score}
        best={best}
        status={status}
        onToggle={toggle}
        onRestart={start}
        extra={`${t('games.lives')} ${lives}`}
      />

      <div className="game-stage">
        <canvas
          ref={canvasRef}
          width={BR_W}
          height={BR_H}
          className="game-canvas game-canvas-breaker"
          role="img"
          aria-label={t('games.breaker.title')}
          onPointerDown={onPointerDown}
          onPointerMove={(event) => {
            // Souris : la raquette suit sans bouton ; tactile : doigt posé.
            if (event.pointerType !== 'touch' || event.buttons > 0 || touchRef.current !== null) {
              movePaddleTo(event.clientX);
            }
          }}
          onPointerUp={() => {
            touchRef.current = null;
          }}
        />
        {status === 'idle' && (
          <StageOverlay tone="info">
            <p className="game-overlay-title">{t('games.breaker.title')}</p>
            <button type="button" className="btn btn-primary game-btn" onClick={start}>
              {t('games.start')}
            </button>
          </StageOverlay>
        )}
        {status === 'paused' && (
          <StageOverlay tone="info">
            <p>{t('games.paused')}</p>
          </StageOverlay>
        )}
        {status === 'over' && (
          <StageOverlay tone="over">
            <p className="game-overlay-title">{t('games.gameOver')}</p>
            <p>
              {t('games.score')} : {score}
            </p>
            <button type="button" className="btn btn-primary game-btn" onClick={start}>
              {t('games.restart')}
            </button>
          </StageOverlay>
        )}
      </div>

      <p className="game-desc">{t('games.breaker.desc')}</p>

      <div className="game-pad game-pad-pong" role="group" aria-label={t('games.breaker.title')}>
        <button type="button" className="game-ctl" data-game-key="left" onClick={() => nudge(-1)}>
          <span aria-hidden="true">◀</span>
        </button>
        <button type="button" className="game-ctl" data-game-key="right" onClick={() => nudge(1)}>
          <span aria-hidden="true">▶</span>
        </button>
      </div>

      <p className="game-hint">{t('games.controlsHint')}</p>
    </section>
  );
}

/* ====================================================================== */
/*  🚐 Tourbus Runner — endless runner, saut d'obstacles                   */
/* ====================================================================== */

const RUN_W = 340;
const RUN_H = 220;
const RUN_GROUND = 178;
const RUN_BUS_X = 46;
const RUN_BUS_W = 56;
const RUN_BUS_H = 30;
const RUN_GRAVITY = 0.62;
const RUN_JUMP_V = -8.4;
const RUN_SPEED0 = 3.0;
const RUN_SPEED_MAX = 8.2;

interface RunnerObstacle {
  x: number;
  w: number;
  h: number;
}

interface RunnerCoffee {
  x: number;
  y: number;
}

export function TourbusRunnerGame() {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const [score, setScore] = useState(0);
  const [best, bumpBest] = useHighScore('bandz.games.high.runner');
  const { status, statusRef, setStatus } = useGameStatus();

  const busYRef = useRef(RUN_GROUND - RUN_BUS_H);
  const vyRef = useRef(0);
  const jumpingRef = useRef(false);
  const speedRef = useRef(RUN_SPEED0);
  const scoreRef = useRef(0);
  const distRef = useRef(0);
  const obstaclesRef = useRef<RunnerObstacle[]>([]);
  const coffeesRef = useRef<RunnerCoffee[]>([]);
  const spawnRef = useRef(60);
  const coffeeSpawnRef = useRef(150);

  const draw = useCallback(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    if (!ctx) return;

    ctx.fillStyle = '#1b1915';
    ctx.fillRect(0, 0, RUN_W, RUN_H);

    // Ciel « nuit de tournée » : deux bandes, aucune image.
    ctx.fillStyle = 'rgba(90, 209, 230, 0.08)';
    ctx.fillRect(0, 0, RUN_W, RUN_GROUND - 40);

    // Bitume + ligne de rive.
    ctx.fillStyle = '#26231d';
    ctx.fillRect(0, RUN_GROUND, RUN_W, RUN_H - RUN_GROUND);
    ctx.strokeStyle = 'rgba(234, 231, 221, 0.28)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, RUN_GROUND + 0.5);
    ctx.lineTo(RUN_W, RUN_GROUND + 0.5);
    ctx.stroke();

    // Barrières douanières (rayures ambre / sombre).
    obstaclesRef.current.forEach((ob) => {
      const top = RUN_GROUND - ob.h;
      for (let i = 0; i < ob.h; i += 6) {
        ctx.fillStyle = i % 12 === 0 ? '#e0a44c' : '#3a352c';
        ctx.fillRect(ob.x, top + i, ob.w, Math.min(6, ob.h - i));
      }
      ctx.fillStyle = '#cfc9b8';
      ctx.fillRect(ob.x, top - 2, ob.w, 2);
    });

    // Cafés bonus : une tasse = un cercle + une anse.
    coffeesRef.current.forEach((c) => {
      ctx.fillStyle = '#f5c469';
      ctx.beginPath();
      ctx.arc(c.x, c.y, 7, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#f5c469';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(c.x + 8, c.y, 4, -Math.PI / 2, Math.PI / 2);
      ctx.stroke();
    });

    // Le tourbus : caisse ambre, vitres cyan, deux roues.
    const y = busYRef.current;
    ctx.fillStyle = '#e0a44c';
    ctx.fillRect(RUN_BUS_X, y, RUN_BUS_W, RUN_BUS_H - 8);
    ctx.fillStyle = '#f5c469';
    ctx.fillRect(RUN_BUS_X, y, RUN_BUS_W, 5);
    ctx.fillStyle = '#5ad1e6';
    ctx.fillRect(RUN_BUS_X + 8, y + 8, 14, 9);
    ctx.fillRect(RUN_BUS_X + 28, y + 8, 14, 9);
    ctx.fillStyle = '#2a2620';
    ctx.fillRect(RUN_BUS_X - 3, y + RUN_BUS_H - 12, RUN_BUS_W + 6, 5);
    ctx.fillStyle = '#12100d';
    ctx.beginPath();
    ctx.arc(RUN_BUS_X + 12, y + RUN_BUS_H - 6, 6, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(RUN_BUS_X + RUN_BUS_W - 12, y + RUN_BUS_H - 6, 6, 0, Math.PI * 2);
    ctx.fill();
  }, []);

  const reset = useCallback(() => {
    busYRef.current = RUN_GROUND - RUN_BUS_H;
    vyRef.current = 0;
    jumpingRef.current = false;
    speedRef.current = RUN_SPEED0;
    scoreRef.current = 0;
    distRef.current = 0;
    obstaclesRef.current = [];
    coffeesRef.current = [];
    spawnRef.current = 60;
    coffeeSpawnRef.current = 150;
    setScore(0);
    draw();
  }, [draw]);

  const endGame = useCallback(() => {
    setStatus('over');
    bumpBest(scoreRef.current);
    draw();
  }, [bumpBest, draw, setStatus]);

  const jump = useCallback(() => {
    if (statusRef.current !== 'running') {
      if (statusRef.current === 'paused') {
        // Reprise : on relance la boucle et on saute dans le même geste.
        setStatus('running');
      } else {
        reset();
        setStatus('running');
      }
    }
    // Un saut demandé pendant l'atterrissage est ignoré : pas de double saut.
    if (jumpingRef.current) return;
    jumpingRef.current = true;
    vyRef.current = RUN_JUMP_V;
  }, [reset, setStatus, statusRef]);

  const step = useCallback(() => {
    if (statusRef.current !== 'running') return;

    // Physique du saut.
    if (jumpingRef.current) {
      busYRef.current += vyRef.current;
      vyRef.current += RUN_GRAVITY;
      if (busYRef.current >= RUN_GROUND - RUN_BUS_H) {
        busYRef.current = RUN_GROUND - RUN_BUS_H;
        vyRef.current = 0;
        jumpingRef.current = false;
      }
    }

    // Vitesse progressive + score de distance.
    speedRef.current = Math.min(RUN_SPEED_MAX, speedRef.current + 0.0012);
    distRef.current += speedRef.current;
    const nextScore = Math.floor(distRef.current / 10);
    if (nextScore !== scoreRef.current) {
      scoreRef.current = nextScore;
      setScore(nextScore);
    }

    // Obstacles.
    spawnRef.current -= 1;
    if (spawnRef.current <= 0) {
      const h = 18 + Math.floor(Math.random() * 16);
      obstaclesRef.current.push({ x: RUN_W + 20, w: 14 + Math.floor(Math.random() * 10), h });
      // Intervalle resserré par la vitesse : plus on va vite, plus il faut d'adresse.
      spawnRef.current = Math.floor(70 - speedRef.current * 4 + Math.random() * 50);
      if (spawnRef.current < 32) spawnRef.current = 32;
    }
    obstaclesRef.current.forEach((ob) => {
      ob.x -= speedRef.current;
    });
    obstaclesRef.current = obstaclesRef.current.filter((ob) => ob.x + ob.w > -10);

    // Cafés bonus.
    coffeeSpawnRef.current -= 1;
    if (coffeeSpawnRef.current <= 0) {
      coffeesRef.current.push({
        x: RUN_W + 20,
        y: RUN_GROUND - RUN_BUS_H - 34 - Math.floor(Math.random() * 22),
      });
      coffeeSpawnRef.current = 120 + Math.floor(Math.random() * 140);
    }
    coffeesRef.current.forEach((c) => {
      c.x -= speedRef.current;
    });

    const bx = RUN_BUS_X;
    const by = busYRef.current;
    coffeesRef.current = coffeesRef.current.filter((c) => {
      const hit =
        c.x + 9 >= bx && c.x - 9 <= bx + RUN_BUS_W && c.y + 9 >= by && c.y - 9 <= by + RUN_BUS_H;
      if (hit) {
        scoreRef.current += 25;
        distRef.current += 250; // le score de distance ne régresse pas du bonus
        setScore(scoreRef.current);
        return false;
      }
      return c.x > -20;
    });

    // Collision barrière : boîte du bus contre boîte de l'obstacle.
    const crashed = obstaclesRef.current.some(
      (ob) =>
        bx + RUN_BUS_W - 4 > ob.x &&
        bx + 4 < ob.x + ob.w &&
        by + RUN_BUS_H - 2 > RUN_GROUND - ob.h,
    );
    if (crashed) {
      endGame();
      return;
    }

    draw();
  }, [draw, endGame, statusRef]);

  const start = useCallback(() => {
    if (statusRef.current === 'paused') {
      setStatus('running');
      return;
    }
    reset();
    setStatus('running');
  }, [reset, setStatus, statusRef]);

  const toggle = useCallback(() => {
    if (statusRef.current === 'running') setStatus('paused');
    else start();
  }, [setStatus, start, statusRef]);

  useEffect(() => {
    reset();
    const id = window.setInterval(step, 16);
    return () => window.clearInterval(id);
  }, [reset, step]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === ' ' || event.key === 'ArrowUp' || event.key.toLowerCase() === 'w') {
        event.preventDefault();
        jump();
      } else if (event.key === 'p') {
        event.preventDefault();
        toggle();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [jump, toggle]);

  return (
    <section className="game-panel" data-game="runner" data-game-panel="runner">
      <ScoreBar score={score} best={best} status={status} onToggle={toggle} onRestart={start} extra={`${t('games.cups')} ${Math.floor(score / 25)}`} />

      <div className="game-stage">
        <canvas
          ref={canvasRef}
          width={RUN_W}
          height={RUN_H}
          className="game-canvas game-canvas-runner"
          role="img"
          aria-label={t('games.runner.title')}
          onPointerDown={jump}
        />
        {status === 'idle' && (
          <StageOverlay tone="info">
            <p className="game-overlay-title">{t('games.runner.title')}</p>
            <button type="button" className="btn btn-primary game-btn" onClick={start}>
              {t('games.start')}
            </button>
          </StageOverlay>
        )}
        {status === 'paused' && (
          <StageOverlay tone="info">
            <p>{t('games.paused')}</p>
          </StageOverlay>
        )}
        {status === 'over' && (
          <StageOverlay tone="over">
            <p className="game-overlay-title">{t('games.gameOver')}</p>
            <p>
              {t('games.score')} : {score}
            </p>
            <button type="button" className="btn btn-primary game-btn" onClick={start}>
              {t('games.restart')}
            </button>
          </StageOverlay>
        )}
      </div>

      <p className="game-desc">{t('games.runner.desc')}</p>

      <div className="game-pad game-pad-runner" role="group" aria-label={t('games.runner.title')}>
        <button type="button" className="game-ctl game-ctl-wide" data-game-key="jump" onClick={jump}>
          <span aria-hidden="true">⬆</span> {t('games.jump')}
        </button>
      </div>

      <p className="game-hint">{t('games.controlsHint')}</p>
    </section>
  );
}

/* ====================================================================== */
/*  🎹 Kaoss Synth X/Y — pad tactile + filtre résonant + delay            */
/* ====================================================================== */

const KX_W = 320;
const KX_H = 260;

export type KaossScaleId = 'minorPentatonic' | 'phrygian' | 'dorian' | 'chromatic';

/** Degrés (demi-tons) par gamme — le pad en couvre DEUX octaves. */
export const KAOSS_SCALES: Record<KaossScaleId, number[]> = {
  minorPentatonic: [0, 3, 5, 7, 10],
  phrygian: [0, 1, 3, 5, 7, 8, 10],
  dorian: [0, 2, 3, 5, 7, 9, 10],
  chromatic: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
};

export type KaossWaveId = 'saw' | 'square' | 'sub';

type AudioContextCtor = typeof AudioContext;

function audioCtor(): AudioContextCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as Window & { webkitAudioContext?: AudioContextCtor };
  return window.AudioContext ?? w.webkitAudioContext ?? null;
}

/** Fréquence MIDI → Hz (A4 = 440 Hz, comme tous les instruments). */
export function midiToHz(midi: number): number {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

interface KaossVoice {
  osc: OscillatorNode;
  filter: BiquadFilterNode;
  gain: GainNode;
}

export function KaossSynthPanel() {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const [scale, setScale] = useState<KaossScaleId>('minorPentatonic');
  const [wave, setWave] = useState<KaossWaveId>('saw');
  const [latch, setLatch] = useState(false);
  const [note, setNote] = useState<string>('—');
  const [cutoff, setCutoff] = useState(0);

  const ctxRef = useRef<AudioContext | null>(null);
  const masterRef = useRef<GainNode | null>(null);
  const fxInputRef = useRef<GainNode | null>(null);
  const voiceRef = useRef<KaossVoice | null>(null);
  const lastRef = useRef({ x: 0.5, y: 0.5 });
  const scaleRef = useRef<KaossScaleId>(scale);
  const waveRef = useRef<KaossWaveId>(wave);
  const latchRef = useRef(false);
  const lastNoteRef = useRef(-1);
  scaleRef.current = scale;
  waveRef.current = wave;
  latchRef.current = latch;

  const draw = useCallback(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    if (!ctx) return;

    ctx.fillStyle = '#171512';
    ctx.fillRect(0, 0, KX_W, KX_H);

    // Grille de notes : chaque colonne est une touche de la gamme (2 octaves).
    const degrees = KAOSS_SCALES[scaleRef.current];
    const columns = degrees.length * 2;
    const colW = KX_W / columns;
    for (let i = 0; i < columns; i += 1) {
      ctx.fillStyle = i % degrees.length === 0 ? 'rgba(245, 196, 105, 0.10)' : 'rgba(234, 231, 221, 0.04)';
      ctx.fillRect(i * colW, 0, Math.max(1, colW - 1), KX_H);
    }

    // Lignes d'octave.
    ctx.strokeStyle = 'rgba(234, 231, 221, 0.18)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(KX_W / 2, 0);
    ctx.lineTo(KX_W / 2, KX_H);
    ctx.stroke();

    // Curseur : un halo ambre dont la taille suit la résonance (axe Y).
    const px = lastRef.current.x * KX_W;
    const py = lastRef.current.y * KX_H;
    ctx.fillStyle = 'rgba(224, 164, 76, 0.22)';
    ctx.beginPath();
    ctx.arc(px, py, 26, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#f5c469';
    ctx.beginPath();
    ctx.arc(px, py, 7, 0, Math.PI * 2);
    ctx.fill();

    // Repères : axe X = hauteur, axe Y = filtre (haut = fermé).
    ctx.fillStyle = 'rgba(234, 231, 221, 0.45)';
    ctx.font = '9px ui-monospace, monospace';
    ctx.textAlign = 'left';
    ctx.fillText('X · PITCH', 8, 14);
    ctx.textAlign = 'right';
    ctx.fillText('Y · FILTER', KX_W - 8, 14);
  }, []);

  const ensureAudio = useCallback((): AudioContext | null => {
    const Ctor = audioCtor();
    if (!Ctor) return null;
    if (!ctxRef.current) {
      const ctx = new Ctor();
      const master = ctx.createGain();
      master.gain.value = 0.7;
      master.connect(ctx.destination);

      // Bus d'effet : un delay « dub » (3/16 s) avec feedback doux. Le retour
      // part directement à la destination, jamais dans le master : pas de
      // boucle master → delay → master qui s'auto-amplifierait.
      const fxIn = ctx.createGain();
      fxIn.gain.value = 1;
      const delay = ctx.createDelay(1.5);
      delay.delayTime.value = 0.28;
      const feedback = ctx.createGain();
      feedback.gain.value = 0.32;
      const wet = ctx.createGain();
      wet.gain.value = 0.26;
      fxIn.connect(delay);
      delay.connect(feedback);
      feedback.connect(delay);
      delay.connect(wet);
      wet.connect(ctx.destination);

      masterRef.current = master;
      fxInputRef.current = fxIn;
      ctxRef.current = ctx;
    }
    if (ctxRef.current.state === 'suspended') void ctxRef.current.resume();
    return ctxRef.current;
  }, []);

  const stopVoice = useCallback(() => {
    const voice = voiceRef.current;
    const ctx = ctxRef.current;
    if (!voice || !ctx) return;
    try {
      voice.gain.gain.cancelScheduledValues(ctx.currentTime);
      voice.gain.gain.setTargetAtTime(0, ctx.currentTime, 0.02);
      voice.osc.stop(ctx.currentTime + 0.12);
    } catch {
      /* déjà arrêté */
    }
    voiceRef.current = null;
    lastNoteRef.current = -1;
    setNote('—');
  }, []);

  /** Joue (ou module) la voix courante selon la position X/Y normalisée. */
  const playAt = useCallback(
    (nx: number, ny: number) => {
      lastRef.current = { x: nx, y: ny };
      const ctx = ensureAudio();
      if (!ctx) return;

      const degrees = KAOSS_SCALES[scaleRef.current];
      const total = degrees.length * 2;
      const idx = clamp(Math.floor(nx * total), 0, total - 1);
      const degree = degrees[idx % degrees.length] + 12 * Math.floor(idx / degrees.length);
      const midi = 45 + degree; // A2 comme note de départ
      const hz = midiToHz(midi);
      // Axe Y inversé : vers le haut on OUVRE le filtre (grave = fermé).
      const cut = 180 * Math.pow(2, (1 - ny) * 5.4);
      const q = 1.2 + (1 - ny) * 14;

      setCutoff(Math.round(cut));
      const names = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
      setNote(`${names[midi % 12]}${Math.floor(midi / 12) - 1}`);

      const voice = voiceRef.current;
      if (!voice) {
        const master = masterRef.current;
        if (!master) return;
        const osc = ctx.createOscillator();
        const filter = ctx.createBiquadFilter();
        const gain = ctx.createGain();
        osc.type =
          waveRef.current === 'square' ? 'square' : waveRef.current === 'sub' ? 'triangle' : 'sawtooth';
        osc.frequency.value = waveRef.current === 'sub' ? hz / 2 : hz;
        filter.type = 'lowpass';
        filter.frequency.value = cut;
        filter.Q.value = q;
        gain.gain.value = 0;
        osc.connect(filter);
        filter.connect(gain);
        gain.connect(master);
        // Envoi d'effet : le delay s'entend À CÔTÉ du signal sec, pas à sa place.
        if (fxInputRef.current) gain.connect(fxInputRef.current);
        gain.gain.setTargetAtTime(0.22, ctx.currentTime, 0.02);
        osc.start();
        voiceRef.current = { osc, filter, gain };
        lastNoteRef.current = midi;
        return;
      }

      // Voix déjà vivante : on GLISSE vers la nouvelle note (pas de re-attaque).
      voice.filter.frequency.setTargetAtTime(cut, ctx.currentTime, 0.02);
      voice.filter.Q.setTargetAtTime(q, ctx.currentTime, 0.05);
      const target = waveRef.current === 'sub' ? hz / 2 : hz;
      if (lastNoteRef.current !== midi) {
        voice.osc.frequency.setTargetAtTime(target, ctx.currentTime, 0.015);
        lastNoteRef.current = midi;
      }
    },
    [ensureAudio],
  );

  const posFromEvent = (clientX: number, clientY: number) => {
    const cv = canvasRef.current;
    if (!cv) return null;
    const rect = cv.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;
    return {
      x: clamp((clientX - rect.left) / rect.width, 0, 1),
      y: clamp((clientY - rect.top) / rect.height, 0, 1),
    };
  };

  const onDown = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    const pos = posFromEvent(event.clientX, event.clientY);
    if (!pos) return;
    (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
    playAt(pos.x, pos.y);
  };

  const onMove = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (event.buttons === 0 && event.pointerType === 'mouse') return;
    const pos = posFromEvent(event.clientX, event.clientY);
    if (!pos) return;
    playAt(pos.x, pos.y);
  };

  const onUp = () => {
    if (!latchRef.current) stopVoice();
  };

  // L'état graphique suit le curseur, même quand l'audio est refusé au montage.
  useEffect(() => {
    draw();
  }, [draw, scale]);

  // Changement de forme d'onde à chaud.
  useEffect(() => {
    const voice = voiceRef.current;
    if (voice) {
      voice.osc.type = wave === 'square' ? 'square' : wave === 'sub' ? 'triangle' : 'sawtooth';
    }
  }, [wave]);

  // Démonter le pad coupe le son : pas de note fantôme dans un autre onglet.
  useEffect(
    () => () => {
      const voice = voiceRef.current;
      const ctx = ctxRef.current;
      if (voice && ctx) {
        try {
          voice.osc.stop();
        } catch {
          /* déjà arrêté */
        }
      }
      voiceRef.current = null;
      if (ctx) void ctx.close();
      ctxRef.current = null;
    },
    [],
  );

  const latchToggle = () => {
    const next = !latch;
    setLatch(next);
    latchRef.current = next;
    if (!next) stopVoice();
  };

  return (
    <section className="game-panel kaoss-panel" data-game="kaoss" data-game-panel="kaoss">
      <div className="kaoss-head">
        <span className="kaoss-note" data-kaoss-note>
          {note}
        </span>
        <span className="kaoss-meta">
          {cutoff > 0 ? `${cutoff} Hz` : '—'}
        </span>
      </div>

      <div className="game-stage kaoss-stage">
        <canvas
          ref={canvasRef}
          width={KX_W}
          height={KX_H}
          className="game-canvas game-canvas-kaoss"
          role="img"
          aria-label={t('games.kaoss.title')}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
        />
      </div>

      <div className="kaoss-controls">
        <label className="kaoss-field">
          <span>{t('games.kaoss.scale')}</span>
          <select
            value={scale}
            data-kaoss-scale
            onChange={(event) => setScale(event.target.value as KaossScaleId)}
          >
            <option value="minorPentatonic">{t('games.kaoss.scaleMinorPentatonic')}</option>
            <option value="phrygian">{t('games.kaoss.scalePhrygian')}</option>
            <option value="dorian">{t('games.kaoss.scaleDorian')}</option>
            <option value="chromatic">{t('games.kaoss.scaleChromatic')}</option>
          </select>
        </label>

        <label className="kaoss-field">
          <span>{t('games.kaoss.wave')}</span>
          <select
            value={wave}
            data-kaoss-wave
            onChange={(event) => setWave(event.target.value as KaossWaveId)}
          >
            <option value="saw">{t('games.kaoss.waveSaw')}</option>
            <option value="square">{t('games.kaoss.waveSquare')}</option>
            <option value="sub">{t('games.kaoss.waveSub')}</option>
          </select>
        </label>

        <button
          type="button"
          className={`game-ctl kaoss-latch${latch ? ' on' : ''}`}
          data-kaoss-latch
          aria-pressed={latch}
          onClick={latchToggle}
        >
          {t('games.kaoss.hold')}
        </button>
      </div>

      <p className="game-desc">{t('games.kaoss.desc')}</p>
    </section>
  );
}
